import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Form, Alert, Button } from 'react-bootstrap'
import AssetGroupSelect from '../../Employees/Form/FormComponents/AssetGroupSelect'
import OfficeSelect from '../../Employees/Form/FormComponents/OfficeSelect'
import CostCenterSelect from '../../Employees/Form/FormComponents/CostCenterSelect'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'
import { useLocation, useNavigate } from 'react-router-dom'
import MediaQuery from 'react-responsive'
import { setPointerErrorsFromApiResponse } from "../../../utils/api/apiUtils"
import MandatoryField from '../../MandatoryField/MandatoryField'
import useStores from '../../../stores/useStore'
import ArraySelect from '../../Select/ArraySelect'
import { useAssetTypesState } from '../../../stores/Configuration/AssetTypesProvider'
import useMountEffect from '../../../utils/hooks/useMountEffect';
import { emptyGuid, isEmptyGuid } from '../../../utils/emptyGuid'
import { getMetaProperty } from '../../../utils/customerPrototypes';
import LanguageForm from '../../Forms/LanguageForm'
import LanguageNameInput from '../../Forms/LanguageNameInput'
import OverlayContainer from '../../Page/logix2020/OverlayContainer'
import OverlayRight from '../../Page/logix2020/OverlayRight'
import { cloneDeep } from 'lodash';

const StyledFormWrapper = styled.div`
  max-width: 600px;
  .no-drop {
    cursor: no-drop
  }
  .default  {
    cursor: default
  }
  @media (max-width: 1080px) {
    max-width:none;
  }
`
const AlertWrapper = styled.div`
  margin-top:2rem;
`
const groupTemplate = {
    id: emptyGuid,
    name: {
        values: []
    },
    assetType: {
        id: emptyGuid,
    },
    parent: {
        id: emptyGuid,
    },
    office: {
        id: emptyGuid,
    },
    costcenter: {
        id: emptyGuid,
    }
}
const AssetGroupGeneralForm = ({ groupDetails, mode = "create" }) => {
    const { assetTypes, getAssetTypes } = useAssetTypesState()
    const [overlayVisible, setOverlayVisible] = useState(false)
    const { t } = useTranslation()
    const [group, setGroup] = useState(groupTemplate)
    const [formErrors, setFormErrors] = useState({})
    const mountedRef = useRef(true)
    const { assetGroupStore } = useStores()
    const [redirectId, setRedirectId] = useState("")
    const [successCreated, setSuccessCreated] = useState(false)
    const [successUpdated, setSuccessUpdated] = useState(false)
    const [error, setError] = useState(false)
    const [filteredAssetTypes, setFilteredAssetTypes] = useState([])
    const navigate = useNavigate()
    let location = useLocation()

    useMountEffect(() => {
        if (groupDetails) {
            setGroup(groupDetails)
        }
        getAssetTypes()
    })
    //Cleanup Function
    useEffect(() => {
        return () => { mountedRef.current = false }
    }, [])
    useEffect(() => {
        let cleared = assetTypes.filter(item => !item.isSystemType && item.isGrouped);
        if (cleared.length === 1) {
            setGroup(prev => ({
                ...prev,
                assetType: {
                    ...prev.assetType,
                    id: cleared[0].id
                }
            }))
        }
        setFilteredAssetTypes(cleared);
    }, [assetTypes])
    useEffect(() => {
        if (location.state && location.state.createNewGroup) {
            setSuccessCreated(true)
        }
    }, [location.state])
    const getPreparedGroupData = () => {
        const preparedGroupData = cloneDeep(group);
        ["assetType", "parent", "office", "costcenter"].forEach(obj => {
            if (isEmptyGuid(preparedGroupData[obj]?.id ?? emptyGuid)) {
                preparedGroupData[obj] = null
            }
        })
        return preparedGroupData
    }
    const handleChangeParent = async (groupId) => {
        let parentCostcenter = cloneDeep(group.costcenter)
        let parentOffice = cloneDeep(group.office)
        if (!isEmptyGuid(groupId)) {
            let loadedGroup = await apiClient.getJson(APIEndpoints.assetGroupAdmin(groupId).group)
            parentCostcenter = loadedGroup.costcenter
            parentOffice = loadedGroup.office
        }
        setGroup(prev => ({
            ...prev,
            costcenter: parentCostcenter,
            office: parentOffice,
            parent: {
                ...prev.parent,
                id: groupId
            }
        }));
        resetErrorByFormKey("parent")
        resetErrorByFormKey("office")
        resetErrorByFormKey("costcenter")
    }
    const handleClickSubmit = () => {
        let groupData = getPreparedGroupData()
        setError(false)
        if (mode === "create") {
            apiClient.postJson(
                APIEndpoints.assetGroupAdmin(),
                groupData,
                setSuccessCreated(false),
            ).then((response) => {
                if (!mountedRef.current) { return null }
                setSuccessCreated(true)
                assetGroupStore.resetGroups()
                setRedirectId(response)
            }).catch(error => {
                //setApiError(error)
                setFormErrors(setPointerErrorsFromApiResponse(error))
                setError(true)
            }
            )
        }
        if (mode === "edit") {
            apiClient.patchJson(
                APIEndpoints.assetGroupAdmin(group.id).group,
                groupData,
                setSuccessUpdated(false),
            )
                .then(() => {
                    if (!mountedRef.current) { return null }
                    setSuccessUpdated(true)
                    assetGroupStore.resetGroups()
                })
                .catch((error) => {
                    //setApiError(error)
                    setFormErrors(setPointerErrorsFromApiResponse(error))
                    setError(true)
                })
        }
    }
    const handleCloseOverlay = () => {
        setOverlayVisible(false)
    }
    const updateTranslations = (name) => (translations) => {
        setGroup(prev => ({
            ...prev,
            [name]: {
                ...prev[name],
                values: translations
            }
        }))
        resetErrorByFormKey(name)
    }
    useEffect(() => {
        if (redirectId) {
            navigate(`/assetGroupsAdministration/edit/${redirectId}#general`, { replace: true, state: { createNewGroup: true } })
        }
    }, [navigate, redirectId])
    const resetErrorByFormKey = useCallback(formKey => {
        setFormErrors(prev => {
            if (prev[formKey]) {
                delete prev[formKey]
            }
            return prev
        })
    }, [setFormErrors])
    const fieldIsDisabled = (name) => {
        if (!isEmptyGuid(group.parent?.id ?? emptyGuid) && (name === 'office' || name === 'costcenter')) return true
        let disabled = getMetaProperty(group.metaProperties, `/${name}`, 'isReadOnly')
        return !!disabled
    }
    return (
        <div>
            <StyledFormWrapper>
                <LanguageNameInput
                    id="name"
                    openLanguageModal={() => setOverlayVisible(true)}
                    languageArray={group.name.values}
                    onChange={updateTranslations("name")}
                    label={`${t('name')}*`}
                    disabled={fieldIsDisabled('name')}
                    isInvalid={formErrors && formErrors.name}
                />
                <Form.Group className="mb-3">
                    <Form.Label className="d-block ps-1" >{`${t('assetGroups:assetType')}*`}</Form.Label>
                    <ArraySelect
                        value={group.assetType?.id ?? emptyGuid}
                        options={filteredAssetTypes}
                        noAutoSelect
                        id="assetType"
                        onChange={(e) => {
                            setGroup(prev => ({
                                ...prev,
                                assetType: {
                                    ...prev.assetType,
                                    id: e.target.value
                                }
                            })); resetErrorByFormKey("assetType")
                        }}
                        disabled={fieldIsDisabled('assetType')}
                        isInvalid={formErrors && formErrors.assetType}
                        returnEvent
                    />
                    <Form.Control.Feedback type="invalid">
                        {formErrors.assetType}
                    </Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="d-block ps-1">{t('assetGroups:parentGroup')}</Form.Label>
                    <AssetGroupSelect
                        id="parent"
                        value={group.parent?.id ?? emptyGuid}
                        onChange={handleChangeParent}
                        disabled={fieldIsDisabled('parent')}
                        isInvalid={formErrors && formErrors.parent}
                        errorMessage={formErrors.parent}
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label className="d-block ps-1">{`${t('assetGroups:organizationalUnit')}*`}</Form.Label>
                    <OfficeSelect
                        id="office"
                        value={group.office?.id ?? emptyGuid}
                        onChange={(officeId) => {
                            setGroup(prev => ({
                                ...prev,
                                office: {
                                    ...prev.office,
                                    id: officeId
                                }
                            })); resetErrorByFormKey("office")
                        }}
                        disabled={fieldIsDisabled('office')}
                        errorMessage={formErrors.office}
                        isInvalid={formErrors.office}
                        withSearch
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <CostCenterSelect
                        id="costcenter"
                        label={`${t('assetGroups:costcenter')}*`}
                        value={group.costcenter?.id ?? emptyGuid}
                        onChange={(costCenter) => {
                            setGroup(prev => ({
                                ...prev,
                                costcenter: {
                                    ...prev.costcenter,
                                    id: costCenter.id
                                }
                            })); resetErrorByFormKey("costcenter")
                        }}
                        disabled={fieldIsDisabled('costcenter')}
                        errorMessage={formErrors.costcenter}
                        isInvalid={formErrors.costcenter}
                        withSearch
                    />
                </Form.Group>
                <MediaQuery minWidth={769}>
                    <div className="d-flex justify-content-between">
                        <MandatoryField />
                        <Button onClick={handleClickSubmit} className="pointer" >{t('saveButton')}</Button>
                    </div>
                </MediaQuery>
                <MediaQuery maxWidth={768}>
                    <MandatoryField />
                    <Button onClick={handleClickSubmit} className="pointer w-100" >{t('saveButton')}</Button>
                </MediaQuery>
                <AlertWrapper>
                    {successCreated ?
                        <Alert variant="success">
                            {t('costCenterAdmin:successCreated')}
                        </Alert>
                        : ""
                    }{successUpdated ?
                        <Alert variant="success">
                            {t('costCenterAdmin:successUpdate')}
                        </Alert>
                        : ""
                    }
                    {error &&
                        <Alert variant="danger">{t('articleAdminOverlayProgress:danger')}</Alert>
                    }
                </AlertWrapper>
            </StyledFormWrapper>
            <OverlayContainer>
                <OverlayRight visible={overlayVisible} overLayZIndex={1056} onClose={handleCloseOverlay}>
                    <LanguageForm
                        languageTitle={t('name')}
                        handleCloseOverlay={handleCloseOverlay}
                        languageArray={group ? group.name.values : []}
                        onChange={updateTranslations("name")}
                    />
                </OverlayRight>
            </OverlayContainer>
        </div>
    )
}

export default AssetGroupGeneralForm
